<template>
  <NuxtLink
    v-element-visibility="(visible: boolean) => cardIsVisible = visible"
    class="card-container group bg-white text-neutral font-sans aspect-[10/13] flex flex-col rounded-lg overflow-hidden text-md shadow-lg transition-all"
    :class="{
      'mini-card': miniCard
    }"
    :to="to"
    @touchstart="miniCard && navigateTo(to)"
    @mouseover="mouseOverCard = true"
    @mouseleave="mouseOverCard = false"
  >
    <div
      class="relative w-full min-h-[55%] h-[60%] max-h-[170px] mb-2 overflow-hidden bg-gray-light"
    >
      <div
        v-if="showFavorite && false"
        class="absolute top-1 right-1 w-8 h-8 z-[1] opacity-70 hover:opacity-100 transition-opacity"
      >
        <Icon
          :path="mdiHeart"
          class="z-[1] absolute w-full h-full p-1 pointer-events-none text-black/70 transition-colors"
          :class="{
            'text-primary/90 drop-shadow-lg': favorite
          }"
        />
        <IconButton
          :path="mdiHeartOutline"
          class="z-[1] absolute w-full h-full drop-shadow-lg rounded-full p-1 cursor-pointer text-white/100 transition-colors"
          @click.stop.prevent="favorite = !favorite"
        />
      </div>
      <img
        v-if="!images?.length"
        src="/images/pfs-placeholder.svg"
        loading="lazy"
        :alt="imgAlt || name || 'PFS Realty Group'"
        class="bg-white w-full h-full max-h-[170px] object-contain object-center"
        width="220"
        height="170"
      />
      <img
        v-else-if="images?.length === 1 || hideCarousel || !showSplide"
        :src="
          (images[imageIndex || 0] || images[0]).url ||
          '/images/pfs-placeholder.svg'
        "
        :srcset="(images[imageIndex || 0] || images[0]).set"
        loading="lazy"
        :alt="
          (images[imageIndex || 0] || images[0]).alt ||
          name ||
          'PFS Realty Group'
        "
        class="w-full h-full max-h-[170px] object-cover object-center"
        width="220"
        height="170"
        onerror="javascript:this.src='/images/pfs-placeholder.svg'"
      />
      <Splide
        v-else
        ref="splide"
        :options="{
          perPage: 1,
          perMove: 1,
          pagination: false,
          loop: true,
          lazyLoad: 'nearby',
          role: 'group'
        }"
        class="w-full h-full max-h-[170px] object-cover object-center small-arrows"
        :has-track="false"
      >
        <div class="splide__arrows" @click.stop.prevent></div>
        <SplideTrack class="h-full max-h-[170px]">
          <SplideSlide v-for="(image, index) in images" :key="`${index}-slide`">
            <img
              v-if="index === 0 || index === imageIndex"
              :src="image.url"
              :srcset="image.set"
              loading="lazy"
              :alt="image.alt || name || 'PFS Realty Group'"
              class="w-full h-full max-h-[170px] object-cover object-center"
              width="220"
              height="170"
              onerror="javascript:this.src='/images/pfs-placeholder.svg'"
            />
            <img
              v-else
              :data-splide-lazy="image.url"
              :data-splide-lazy-srcset="image.set"
              :alt="image.alt || name || 'PFS Realty Group'"
              class="w-full h-full max-h-[170px] object-cover object-center"
              width="220"
              height="170"
              onerror="javascript:this.src='/images/pfs-placeholder.svg'"
            />
          </SplideSlide>
        </SplideTrack>
      </Splide>
    </div>
    <component :is="'h' + (h || 2)" class="flex-1 px-4 font-bold leading-5">
      {{ name }}
    </component>
    <p v-if="location" class="px-4 text-sm truncate">
      {{ location }}
    </p>
    <p
      v-if="yearCompletionStr"
      class="px-4 text-sm truncate"
      v-html="
        projectStatus === 'pre-construction' ||
        projectStatus === 'under-construction'
          ? $t('seo.yearCompletion', [yearCompletionStr])
          : projectStatus === 'built' || projectStatus === 'on-the-market'
          ? $t('seo.yearBuilt', [yearCompletionStr])
          : yearCompletionStr
      "
    ></p>
    <p
      class="px-4 mb-2 text-sm truncate mb-2"
      v-html="
        minPrice && !isNaN(+minPrice)
          ? $t('common.fromN', [
              '<span class=&quot;font-bold&quot;>' +
                formatCurrency(+minPrice) +
                '</span>'
            ])
          : $t('search.price') + ' <span class=&quot;font-bold&quot;>TBA</span>'
      "
    ></p>
  </NuxtLink>
</template>

<script setup lang="ts">
import { mdiHeart, mdiHeartOutline } from '@mdi/js';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css/core';
import { vElementVisibility } from '@vueuse/components';

import type { ProjectCardProps } from '@/types/properties';

const IconButton = defineAsyncComponent(
  () => import('@/async-components/Icon/Button.vue')
);

const { language } = useLanguage();

const localePath = useLocalePath();

interface ProjectCardPropsExtended extends ProjectCardProps {
  hideCarousel?: boolean;
  showFavorite?: boolean;
  h?: number;
  miniCard?: boolean;
}

const props = defineProps<ProjectCardPropsExtended>();

const to = computed(() => {
  return props.to ? localePath(props.to) : '';
});

const yearCompletionStr = computed(() => {
  return (
    '<span class="font-bold">' +
    (!props.yearCompletion ? 'TBA' : props.yearCompletion) +
    '</span>'
  );
});

const favorite = defineModel<boolean>('favorite', {
  default: false
});

const splide = ref(null);
const imageIndex = ref(0);

watch(splide, () => {
  if (!splide.value?.splide) {
    return;
  }

  if (imageIndex.value !== splide.value.splide.index) {
    const speed = splide.value.splide.options.speed;
    splide.value.splide.options.speed = 0;
    splide.value.go(imageIndex.value);
    splide.value.splide.options.speed = speed;
  }

  splide.value.splide.on('moved', (newIndex: number) => {
    imageIndex.value = newIndex;
  });
});

const enableSplide = ref(false);

const screenWidth = ref<number | undefined>(undefined);

function formatCurrency(v: number) {
  return '$' + Intl.NumberFormat(language.value).format(v);
}

function setScreenWidth() {
  screenWidth.value = window.innerWidth;
}

const cardIsVisible = ref(false);
const mouseOverCard = ref(false);

const showSplide = computed(() => {
  if (!enableSplide.value) {
    return false;
  }

  if (screenWidth.value < 580) {
    return cardIsVisible.value;
  } else {
    return mouseOverCard.value;
  }
});

onMounted(() => {
  setTimeout(() => (enableSplide.value = true), 500);

  screenWidth.value = window.innerWidth;

  window.addEventListener('resize', setScreenWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', setScreenWidth);
});
</script>

<style lang="scss">
@import '~/assets/css/splide-theme.scss';
</style>

<style scoped lang="scss">
.splide__arrows {
  .splide__arrow--prev {
    left: 0;
  }
}

.card-container:not(.mini-card) {
  @apply sm:min-w-[220px] sm:w-[220px] max-w-[calc(100vw-32px)] min-h-[290px] hover:opacity-90;
}

.card-container.mini-card {
  @apply min-w-[210px] max-w-[220px] min-h-[210px];
  &:hover {
    @apply shadow-xl;
  }
  .image-to-scale {
    transform: none !important;
  }
}
</style>
